import React, { useEffect, useState } from 'react';
import { Dialog } from "@mui/material";
import './AreasModal.scss';
import Spinner from '../../../Spinner';
import axios from 'axios';
import moment from 'moment-timezone';
import AreaAlertsModal from './AreaAlertsModal';
import { useUserContext } from '../../../../contexts/UserContext';
import { useAreasContext } from '../../../../contexts/AreasContext';
import { useAlertsContext } from '../../../../contexts/AlertsContext';

export default function AreasModal(props) {
    const {
        getToken
    } = useUserContext();

    const {
        alerts,
        setAlerts
    } = useAlertsContext();
    
    const {
        modal,
        setModal,
        myAreas,
        setMyAreas,
    } = useAreasContext();

    const [areaName, setAreaName] = useState(modal.content?.area_name);
    const [areaDescription, setAreaDescription] = useState(modal.content?.area_description ? modal.content?.area_description : 'No Description');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        if (alerts.filter((a) => a && a.area_id === modal.content?.area_id).length > 0) {
            setAlert(alerts.filter((a) => a && a.area_id === modal.content?.area_id)[0]);
        }
    }, [alerts]);

    useEffect(() => {
        setAreaName(modal.content?.name);
        setAreaDescription(modal.content?.description ? modal.content?.description : '');
    }, [modal.content]);

    const isBtnDisabled = () => {
        if (areaName === modal.content.area_name && areaDescription === modal.content.area_description) {
            return true;
        } else {
            return false;
        }
    }

    const convertTo12HourFormat = (dateTimeString) => {
        const time = moment.tz(dateTimeString, "UTC");
      
        const localTime = time.tz(moment.tz.guess()).format('YYYY-MM-DD h:mm A');
      
        return localTime;
    }

    const getFormattedUTCTimestamp = () => {
        const now = new Date();
    
        // Extract the UTC date part
        const year = now.getUTCFullYear();
        const month = String(now.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth is zero-based
        const day = String(now.getUTCDate()).padStart(2, '0');
    
        // Extract the UTC time part
        const hours = String(now.getUTCHours()).padStart(2, '0');
        const minutes = String(now.getUTCMinutes()).padStart(2, '0');
        const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    
        // Get milliseconds and format it to 6 digits (microseconds)
        const milliseconds = String(now.getUTCMilliseconds()).padStart(3, '0');
        const microseconds = milliseconds + Math.floor(Math.random() * 1000).toString().padStart(3, '0'); // Add random microseconds
    
        // Combine to the final format
        const formattedTimestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${microseconds}`;
        
        return formattedTimestamp;
    }

    const turnOffNotifications = (alertID) => {
        setLoading(true);
        getToken()
        .then((token) => {
            axios.delete(`https://firewatch-api.space-eyes.com/user/alerts?id=${alertID}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).then((response) => {
                console.log('response: ', response.data);
                setAlerts(alerts.filter((a) => a.id !== modal.content?.area_id));
                setAlert(null);
                setLoading(false);
            }).catch((error) => {
                console.log('error turning off notifications', error);
                setLoading(false);
                setError('Trouble turning off notifications. Please try again.')
            })
        })
        .catch((error) => {
            console.error('Failed to get token:', error);
            setError('Trouble turning off notifications. Please try again.')
        });
    }

    const deleteArea = (id) => {
        if (alert) {
            turnOffNotifications(alert.id)
        }

        setError(false);
        setLoading(true);
        getToken()
        .then((token) => {
            axios.delete(`https://firewatch-api.space-eyes.com/locations?area_id=${modal.content?.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            .then(() => {
                setMyAreas(myAreas.filter((area) => area.id !== modal.content?.id));
                setLoading(false);
                setModal({
                    type: false,
                    content: null
                })
            }).catch((err) => {
                console.log('error deleting area: ', err);
                setError('Trouble deleting area. Please try again.');
                setLoading(false);
            });
        }).catch((error) => {
            console.log('Failed to get token:', error);
            setError('Trouble deleting area. Please try again.');
            setLoading(false);
        });
    };

    const editDetails = () => {
        setLoading(true);
        if (areaName.length > 200) {
            setError('Maximum characters exceeded. Please choose a shorter name.');
            setLoading(false);
        } else if (areaDescription.length > 200) {
            setError('Maximum characters exceeded. Please choose a shorter description.');
            setLoading(false);
        } else {
            getToken()
            .then((token) => {
                axios.post('https://firewatch-api.space-eyes.com/locations', {
                    ...modal.content,
                    id: modal.content?.id,
                    name: areaName,
                    description: areaDescription
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).then(() => {
                    const edits = {
                        ...modal.content,
                        id: modal.content?.id,
                        name: areaName,
                        description: areaDescription
                    }

                    setMyAreas([
                        ...myAreas.filter((area) => area.id !== modal.content?.id),
                        edits
                    ]);

                    setLoading(false);

                    setModal({
                        type: false,
                        content: null
                    })
                }).catch((err) => {
                    console.log('error editing area: ', err);
                    setError('Trouble editing area. Please try again.');
                    setLoading(false);
            });
            })
            .catch((error) => {
                setError('Trouble editing area. Please try again.');
                console.error('Failed to get token:', error);
                setLoading(false);
            });
        }
    }

    const stageSwitch = (stage) => {
        switch (stage) {
            case 'edit-and-delete':
                return (
                    <div className='areas-modal'>
                        <div className='areas-modal-title'>Editing Area</div>
                        <div className='areas-modal-content'>
                            <div className='areas-modal-content-inputs'>
                                <div className='areas-modal-content-inputs-input-container'>
                                    <div className='areas-modal-content-inputs-input-container-title'>Area Name</div>
                                    <input 
                                        type='text' 
                                        className='areas-modal-content-inputs-input-container-input'
                                        value={areaName}
                                        onChange={(e) => setAreaName(e.target.value)}
                                    />
                                </div>
                                <div className='areas-modal-content-inputs-input-container'>
                                    <div className='areas-modal-content-inputs-input-container-title'>Area Description</div>
                                    <input 
                                        type='text' 
                                        className='areas-modal-content-inputs-input-container-input'
                                        value={areaDescription}
                                        onChange={(e) => setAreaDescription(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                       {error && <div className='areas-modal-content-error'>{error}</div>}
                       {loading ?
                       <div style={{ paddingTop: '1rem' }}>
                         <Spinner />
                       </div>
                       :
                       <div className='areas-modal-bottom-btns'>
                            <div 
                                className='areas-modal-bottom-btns-btn-cancel'
                                onClick={() => {
                                    setModal({
                                        type: false,
                                        content: null
                                    })
                                }}>
                                Cancel
                            </div>
                            <button 
                                className='areas-modal-bottom-btns-btn-save'
                                disabled={isBtnDisabled()}
                                style={isBtnDisabled() ? { cursor: 'not-allowed', background: 'gray' } :  { } }
                                onClick={editDetails}
                                >
                                Save Changes
                            </button>
                        </div>}
                        <div 
                            className='areas-modal-delete-btn'
                            onClick={() => {
                                setModal({
                                    type: 'delete-confirmation',
                                    content: modal.content
                                })
                            }}
                            >
                            DELETE AREA
                        </div>
                    </div>
                )
            case 'delete-confirmation':
                return (
                    <div className='areas-modal'>
                        <div className='areas-modal-title'>Deleting Area</div>
                        <div className='areas-modal-content'>
                            <div className='areas-modal-content-text'>Are you sure you want to delete this area?</div>
                        </div>
                        {error && <div className='areas-modal-content-error'>{error}</div>}
                       {loading ?
                       <div style={{ paddingTop: '1rem' }}>
                        <Spinner />
                       </div>
                       :
                       <div className='areas-modal-bottom-btns'>
                            <div 
                                className='areas-modal-bottom-btns-btn-save'
                                onClick={() => {
                                    setModal({
                                        type: false,
                                        content: null
                                    })
                                }}>
                                Cancel
                            </div>
                            <div 
                                className='areas-modal-bottom-btns-btn-cancel'
                                onClick={() => deleteArea(modal.content?.area_id)}
                                >
                                Yes
                            </div>
                        </div>}
                    </div>
                )
            case 'alerts':
                return (
                   <AreaAlertsModal
                        modal={modal}
                        setModal={setModal}
                        alerts={alerts}
                        setAlerts={setAlerts}
                        alert={alert}
                        setAlert={setAlert}
                   />
                )
        }
    }

    return (
        <Dialog
            open={modal.type}
            onClose={() => {
                if (modal.type) {
                    setModal({
                        type: false,
                        content: null
                    })
                }
            }}
            PaperProps={{
                sx: {
                  backgroundColor: "white",
                }
              }}
        >
            {stageSwitch(modal.type)}
        </Dialog>
    )
}